import React, { useEffect, useRef, useState } from 'react';
import { Input, Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectPhoneCodeAndEmoji } from '../../redux';
import { updateShippingAddressCheckBox } from '../../services';
import { ShippingTelEndBoxProps } from '../interfaces';
import './shippingTelEndBox.scss';

const ShippingTelEndBox: React.FC<ShippingTelEndBoxProps> = ({
  initialTelephone,
  initialUseAddressForBilling,
  onUpdate,
  updateButtonClicked
}) => {
  const dispatch = useAppDispatch();
  const { phoneCode, emoji } = useAppSelector(selectPhoneCodeAndEmoji);
  const shipping = useAppSelector((state) => state.user.shipping);
  const selectedAddress = useAppSelector((state) => state.user.selectedAddress);
  
  const [localTelephone, setLocalTelephone] = useState<string>(initialTelephone ?? '');
  const [localUseAddressForBilling, setLocalUseAddressForBilling] = useState(initialUseAddressForBilling);
  const [prevUpdateClicked, setPrevUpdateClicked] = useState(false);
  const prevLocalTelephone = useRef(localTelephone);
  const [errors, setErrors] = useState<{ telephone: boolean; }>({telephone: false});
  const safeId = shipping?.id || null;

  useEffect(() => {
    setLocalTelephone(initialTelephone ?? '');
    setLocalUseAddressForBilling(initialUseAddressForBilling);
  }, [initialTelephone, initialUseAddressForBilling, selectedAddress]);

  useEffect(() => {
    if (updateButtonClicked && !prevUpdateClicked) {
      const hasTelephoneError = !localTelephone.trim() || !validateTelephone(localTelephone);
      setErrors({
        telephone: hasTelephoneError,
      });

      if (!hasTelephoneError) {
        onUpdate({ telephone: localTelephone, phoneCode, emoji });
      }

      setPrevUpdateClicked(true);
    } else if (!updateButtonClicked) {
      setPrevUpdateClicked(false);
    }
  }, [emoji, phoneCode, updateButtonClicked, localTelephone, onUpdate, prevUpdateClicked]);

  useEffect(() => {
    if (localTelephone !== prevLocalTelephone.current && (phoneCode || emoji)) {
      const hasError = !validateTelephone(localTelephone);
      setErrors((prev) => ({ ...prev, telephone: hasError }));
      onUpdate({
        telephone: localTelephone,
        phoneCode,
        emoji,
      });
      prevLocalTelephone.current = localTelephone;
    }
  }, [localTelephone, phoneCode, emoji, onUpdate]);

  const validateTelephone = (value: string): boolean => {
    const normalizedValue = value.replace(/[+\s().-]/g, '');
    const phoneRegex = /^[0-9]{7,15}$/;
    return phoneRegex.test(normalizedValue);
  };

  const handleTelephoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalTelephone(value);
    const hasError = !validateTelephone(value);
    setErrors((prev) => ({ ...prev, telephone: hasError }));
    onUpdate({
      telephone: value,
      phoneCode,
      emoji,
    });
  };

  const handleCheckboxChange = async (e: CheckboxChangeEvent) => {
    const newUseAddressForBilling = e.target.checked;
    setLocalUseAddressForBilling(newUseAddressForBilling);

    onUpdate({
      useAddressForBilling: newUseAddressForBilling,
    });

    if (!safeId) {
      console.error('Shipping address ID is required but is undefined.');
      return;
    }

    const where = { id: safeId };
    const data = { useAddressForBilling: newUseAddressForBilling };

    try {
      const resultAction = await dispatch(updateShippingAddressCheckBox({ where, data }));
      if (updateShippingAddressCheckBox.fulfilled.match(resultAction)) {
      } else {
        console.error('Failed to update billing address:', resultAction.payload);
      }
    } catch (error) {
      console.error('An error occurred while updating billing address:', error);
    }
  };

  const normalizedPhoneCode = String(phoneCode || '').trim();
  const displayPhoneCode = normalizedPhoneCode
    ? normalizedPhoneCode.startsWith('+')
      ? normalizedPhoneCode
      : `+${normalizedPhoneCode}`
    : '';

  return (
    <div className="shipping-tel-end-checkbox">
      <div className="input-container">
        <label className="input-label" htmlFor="telephone">Telephone</label>
        <div className="telephone-input-wrapper">
          {(phoneCode || emoji) && (
            <div className="telephone-prefix">
              {emoji} {displayPhoneCode}
            </div>
          )}
          <Input
            id="telephone"
            name="telephone"
            placeholder="Telephone"
            value={localTelephone ?? ''}  
            onChange={handleTelephoneChange}
            status={errors.telephone ? 'error' : undefined}
          />
        </div>
      </div>
      <div className="checkbox-container">
        <Checkbox
          checked={localUseAddressForBilling}
          onChange={handleCheckboxChange}
        >
          Use address for billing
        </Checkbox>
      </div>
    </div>
  );
};

export default ShippingTelEndBox;
